// @flow
import GeometryType from 'ol/geom/GeometryType';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Circle from 'ol/geom/Circle';
import LineString from 'ol/geom/LineString';
import { SHAPES } from '../../server/shared/constants/zoneConstants';
import type { ShapeType, Shape, ZoneData, Zone } from '../types';
import type { Feature as FeatureType } from '../modules/ui/openlayers';
import { fromLatLon, toLatLon } from './index';

export const getType = (type: ShapeType) => {
    switch(type) {
        case SHAPES.polygon:
            return GeometryType.POLYGON;

        case SHAPES.circle:
            return GeometryType.CIRCLE;

        case SHAPES.line:
            return GeometryType.LINE_STRING;

        default:
            throw new Error(`Unsupported geometry type ${type}`);
    }
};

export const getShape = (feature: FeatureType, type: ShapeType, finished?: boolean) => {
    const geometry = feature.getGeometry();

    switch(type) {
        case SHAPES.polygon: {
            let coordinates = geometry.getCoordinates();

            if(coordinates) {
                coordinates = coordinates[0];
            }
            else {
                coordinates = [
                    []
                ];
            }

            // OpenLayers shows also final point which is the same as starting point and also a temporary point which is not yet set
            // So we have to remove one or two last points
            coordinates = coordinates.slice(0, (finished || coordinates.length < 3) ? -1 : -2);

            if(coordinates.length < 3) {
                for(let i = 0, length = 3 - coordinates.length; i < length; i += 1) {
                    coordinates.push([]);
                }
            }

            coordinates = coordinates.map(coordinate => toLatLon(coordinate));

            return { coordinates };
        }

        case SHAPES.circle:
            return {
                center: toLatLon(geometry.getCenter()),
                radius: geometry.getRadius()
            };

        case SHAPES.line: {
            let coordinates = geometry.getCoordinates() || [];

            // OpenLayers shows also a temporary point which is not yet set
            // So we have to remove last point
            if(!finished) {
                coordinates = coordinates.slice(0, -1);
            }

            if(coordinates.length < 2) {
                for(let i = 0, length = 2 - coordinates.length; i < length; i += 1) {
                    coordinates.push([]);
                }
            }

            coordinates = coordinates.map(coordinate => toLatLon(coordinate));

            return { coordinates };
        }


        default:
            throw new Error(`Unsupported geometry type ${type}`);
    }
};

export const isShapeEqual = (shape1: Shape, shape2: Shape, type: ShapeType) => {
    switch(type) {
        case SHAPES.polygon:
        case SHAPES.line: {
            if(shape1.coordinates.length !== shape2.coordinates.length) {
                return false;
            }

            for(let i = 0, length = shape1.coordinates.length; i < length; i += 1) {
                if(shape1.coordinates[i][0] !== shape2.coordinates[i][0] || shape1.coordinates[i][1] !== shape2.coordinates[i][1]) {
                    return false;
                }
            }

            return true;
        }

        case SHAPES.circle:
            if(shape1.radius !== shape2.radius) {
                return false;
            }
            else if(shape1.center[0] !== shape2.center[0] || shape1.center[1] !== shape2.center[1]) {
                return false;
            }
            else {
                return true;
            }

        default:
            throw new Error(`Unsupported geometry type ${type}`);
    }
};

const findNearestFullPoint = (points, index) => {
    for(let i = index - 1; i !== index; i -= 1) {
        if(i < 0) {
            i = points.length - 1;

            if(i === index) {
                break;
            }
        }

        const point = points[i];

        if(typeof point[0] !== 'undefined' && typeof point[1] !== 'undefined') {
            return point;
        }
    }

    return null;
};

export const createGeometry = (type: ShapeType, shape: Shape) => {
    switch(type) {
        case SHAPES.polygon: {
            let points = [...shape.coordinates];
            for(let i = 0, length = points.length; i < length; i += 1) {
                const point = points[i];
                if(typeof point[0] === 'undefined' || typeof point[1] === 'undefined') {
                    const nearestFullPoint = findNearestFullPoint(points, i);

                    if(nearestFullPoint) {
                        points[i] = nearestFullPoint;
                    }
                }
            }

            points.push(points[0]);

            points = points.map(point => fromLatLon(point));

            return new Polygon([points]);
        }

        case SHAPES.circle: {
            const center = [...shape.center];
            const radius = (shape.radius === null || isNaN(shape.radius)) ? 10 : shape.radius;

            if (typeof center[0] === 'undefined' && typeof center[1] !== 'undefined') {
                center[0] = center[1];
            }
            if (typeof center[1] === 'undefined' && typeof center[0] !== 'undefined') {
                center[1] = center[0];
            }

            return new Circle(fromLatLon(center), radius);
        }

        case SHAPES.line: {
            let points = [...shape.coordinates];
            for(let i = 0, length = points.length; i < length; i += 1) {
                const point = points[i];
                if(typeof point[0] === 'undefined' || typeof point[1] === 'undefined') {
                    const nearestFullPoint = findNearestFullPoint(points, i);

                    if(nearestFullPoint) {
                        points[i] = nearestFullPoint;
                    }
                }
            }

            points = points.map(point => fromLatLon(point));

            return new LineString(points);
        }

        default:
            throw new Error(`Unsupported geometry type ${type}`);
    }
};

export const createFeature = (type: ShapeType, shape: Shape) => {
    return new Feature(createGeometry(type, shape));
};

const fromLatLonToArray = (coord: $ReadOnly<{lat: number, lon: number}>) => {
    return [coord.lat, coord.lon];
};
const fromArrayToLatLon = (coord: [number, number]) => {
    return {lat: coord[0], lon: coord[1]};
};

const convertZoneData = (data, convertCooridnateFn) => {
    const featureData = data.feature;

    let feature;
    if(featureData.type === SHAPES.polygon || featureData.type === SHAPES.line) {
        feature = {
            type: featureData.type,
            coordinates: featureData.coordinates.map(coord => convertCooridnateFn(coord))
        };
    }
    else if(featureData.type === SHAPES.circle) {
        feature = {
            type: featureData.type,
            center: convertCooridnateFn(featureData.center),
            radius: featureData.radius
        };
    }
    else {
        throw new Error(`Unknown shape type ${featureData.type}`);
    }

    return {
        ...data,
        feature
    };
};

export const convertFromZoneData = (data: ZoneData): Zone => {
    return convertZoneData(data, fromLatLonToArray);
};

export const convertToZoneData = (data: Zone): ZoneData => {
    return convertZoneData(data, fromArrayToLatLon);
};

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;

export const toDurationObject = (duration: number) => {
    const hours = Math.floor(duration / HOUR);
    const minutes = Math.floor((duration - hours * HOUR) / MINUTE);
    const seconds = Math.floor((duration - hours * HOUR - minutes * MINUTE) / SECOND);

    return {
        hours,
        minutes,
        seconds
    };
};

export const fromDurationObject = (durationObject: $ReadOnly<{}>) => {
    return ((durationObject.hours * HOUR) + (durationObject.minutes * MINUTE) + (durationObject.seconds * SECOND));
};